import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", brand: "", category: "rice", subcategory: "", pack_size: "", price: 0, mrp: 0, image: "", stock: 100, eligible_for_points: true, active: true };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/products").then(({ data }) => setProducts(data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: k === "price" || k === "mrp" || k === "stock" ? Number(e.target.value) : e.target.value });

  const save = async () => {
    try {
      if (editId) await api.put(`/admin/products/${editId}`, form);
      else await api.post("/admin/products", form);
      toast.success("Saved");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Removed"); load(); };
  const edit = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.product_id); setOpen(true); };

  return (
    <AdminLayout title="Products">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditId(null); } }}>
          <DialogTrigger asChild><Button data-testid="add-product-btn"><Plus size={16} /> Add Product</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} product</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="product-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Brand</Label><Input value={form.brand} onChange={set("brand")} /></div>
                <div><Label>Category</Label>
                  <select value={form.category} onChange={set("category")} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="product-category-input">
                    <option value="rice">Rice</option><option value="grains">Grains</option><option value="pulses">Pulses</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Pack size</Label><Input value={form.pack_size} onChange={set("pack_size")} placeholder="5 kg" /></div>
                <div><Label>Subcategory</Label><Input value={form.subcategory} onChange={set("subcategory")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price ₹</Label><Input type="number" value={form.price} onChange={set("price")} data-testid="product-price-input" /></div>
                <div><Label>MRP ₹</Label><Input type="number" value={form.mrp} onChange={set("mrp")} /></div>
              </div>
              <div><Label>Image URL</Label><Input value={form.image} onChange={set("image")} /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-product-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {products.map((p) => (
          <div key={p.product_id} className="bg-card border border-border rounded-lg p-3 flex gap-3" data-testid={`admin-product-${p.product_id}`}>
            <img src={p.image} alt="" className="w-16 h-16 rounded-md object-cover bg-muted" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground uppercase">{p.category}</div>
              <div className="font-heading font-semibold text-sm leading-tight">{p.name}</div>
              <div className="font-mono-points font-bold">{money(p.price)}</div>
              <div className="mt-1 flex gap-2">
                <button className="text-xs text-primary font-semibold" onClick={() => edit(p)} data-testid={`edit-${p.product_id}`}>Edit</button>
                <button className="text-xs text-destructive font-semibold flex items-center gap-1" onClick={() => del(p.product_id)} data-testid={`del-${p.product_id}`}><Trash2 size={12} /> Remove</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
